<script>
  import { tick } from 'svelte'

  // Reusable searchable select. Options are loaded client-side and filtered as
  // you type (matches label and optional sub text). Keyboard: ↑/↓ move, Enter
  // picks, Escape closes. Big tap targets for mobile. Flips up when there's no
  // room below.
  let {
    value = '',
    options = [],          // [{ value, label, sub? }]
    placeholder = 'Select…',
    noneLabel = null,      // when set, a clear option ('') is shown at the top
    disabled = false,
    id = null,
    onChange = (_v) => {},
    // Optional snippet: (option) => icon markup, drawn to the left of each
    // option and of the selected value in the trigger. When omitted the select
    // looks exactly as before (used by the location selects).
    renderIcon = null
  } = $props()

  let open = $state(false)
  let query = $state('')
  let active = $state(-1)
  let flipUp = $state(false)
  let root                 // the .ss element
  let inputEl              // the search input
  let listEl              // the options scroll container

  // The full option list including the optional "none" entry.
  const allOptions = $derived(
    noneLabel ? [{ value: '', label: noneLabel, none: true }, ...options] : options
  )

  const filteredOptions = $derived.by(() => {
    const q = query.trim().toLowerCase()
    if (!q) return allOptions
    return allOptions.filter(
      (o) => o.none || o.label.toLowerCase().includes(q) || (o.sub || '').toLowerCase().includes(q)
    )
  })

  const currentOption = $derived(allOptions.find((o) => o.value === value && !o.none) || null)
  const currentLabel = $derived(currentOption ? currentOption.label : '')

  async function openPanel() {
    if (disabled) return
    open = true
    query = ''
    // Decide flip direction based on available space below the trigger.
    if (root) {
      const rect = root.getBoundingClientRect()
      const below = window.innerHeight - rect.bottom
      flipUp = below < 320 && rect.top > below
    }
    active = filteredOptions.findIndex((o) => o.value === value)
    await tick()
    inputEl?.focus()
    scrollActiveIntoView()
  }

  function closePanel() {
    open = false
  }

  function pick(v) {
    onChange(v)
    closePanel()
  }

  function scrollActiveIntoView() {
    const el = listEl?.querySelector('.ss-option.active')
    if (el) el.scrollIntoView({ block: 'nearest' })
  }

  function onInput(e) {
    query = e.target.value
    active = 0
    scrollActiveIntoView()
  }

  function onKeydown(e) {
    const opts = filteredOptions
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      active = Math.min(active + 1, opts.length - 1)
      scrollActiveIntoView()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      active = Math.max(active - 1, 0)
      scrollActiveIntoView()
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (opts[active]) pick(opts[active].value)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      closePanel()
    }
  }

  // Close when clicking outside.
  function onWindowClick(e) {
    if (open && root && !root.contains(e.target)) closePanel()
  }
</script>

<svelte:window onclick={onWindowClick} />

<div class="ss" class:open bind:this={root}>
  <button
    type="button"
    {id}
    class="ss-trigger"
    class:disabled
    {disabled}
    onclick={() => (open ? closePanel() : openPanel())}
    aria-haspopup="listbox"
    aria-expanded={open}
  >
    {#if renderIcon && currentOption}
      <span class="ss-icon">{@render renderIcon(currentOption)}</span>
    {/if}
    <span class="val" class:placeholder={!currentLabel}>{currentLabel || placeholder}</span>
    <svg class="chev" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
  </button>

  {#if open}
    <div class="ss-panel" class:up={flipUp} role="listbox">
      <div class="ss-search">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4-4"/></svg>
        <input
          bind:this={inputEl}
          type="text"
          placeholder="Search…"
          value={query}
          oninput={onInput}
          onkeydown={onKeydown}
        />
      </div>
      <div class="ss-options" class:has-icons={!!renderIcon} bind:this={listEl}>
        {#if filteredOptions.length === 0}
          <div class="ss-empty">No matches for “{query}”</div>
        {:else}
          {#each filteredOptions as option, i (option.value)}
            <button
              type="button"
              class="ss-option"
              class:none={option.none}
              class:selected={option.value === value}
              class:active={i === active}
              onclick={() => pick(option.value)}
              onmousemove={() => (active = i)}
              role="option"
              aria-selected={option.value === value}
            >
              {#if renderIcon && !option.none}
                <span class="ss-icon">{@render renderIcon(option)}</span>
              {/if}
              <span class="label">
                {option.label}{#if option.sub}<span class="sub"> · {option.sub}</span>{/if}
              </span>
              <svg class="tick" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </button>
          {/each}
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .ss { position: relative; }
  .ss-trigger {
    width: 100%; display: flex; align-items: center; gap: 8px;
    padding: 10px 12px; border: 1px solid var(--color-gray-300, #d1d5db); border-radius: 8px;
    background: white; cursor: pointer; font-size: 15px; color: #111827; text-align: left;
    transition: border-color .12s, box-shadow .12s;
  }
  .ss-trigger:hover:not(.disabled) { border-color: #9ca3af; }
  .ss-trigger.disabled { background: #f3f4f6; color: #6b7280; cursor: not-allowed; }
  .ss.open .ss-trigger { border-color: var(--color-brand-600); box-shadow: 0 0 0 3px rgba(5,150,105,.15); }
  .ss-trigger .val { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .ss-trigger .val.placeholder { color: #9ca3af; }
  .ss-trigger .chev { color: #9ca3af; flex-shrink: 0; transition: transform .15s; }
  .ss.open .ss-trigger .chev { transform: rotate(180deg); }

  .ss-panel {
    position: absolute; z-index: 40; top: calc(100% + 6px); left: 0; right: 0;
    background: white; border: 1px solid #e5e7eb; border-radius: 12px;
    box-shadow: 0 12px 32px -8px rgba(0,0,0,.22); overflow: hidden;
  }
  .ss-panel.up { top: auto; bottom: calc(100% + 6px); }

  .ss-search { padding: 10px; border-bottom: 1px solid #f3f4f6; position: relative; }
  .ss-search svg { position: absolute; left: 20px; top: 50%; transform: translateY(-50%); color: #9ca3af; width: 16px; height: 16px; }
  .ss-search input {
    width: 100%; padding: 9px 12px 9px 34px; border: 1px solid #e5e7eb;
    border-radius: 8px; font-size: 15px; outline: none; background: #f9fafb;
  }
  .ss-search input:focus { border-color: var(--color-brand-500); background: white; }

  .ss-options { max-height: 280px; overflow-y: auto; padding: 6px; }
  .ss-option {
    display: flex; align-items: center; gap: 10px; width: 100%;
    padding: 11px 12px; border-radius: 8px; cursor: pointer; border: none; background: none;
    font-size: 15px; color: #1f2937; text-align: left;
  }
  .ss-option .tick { width: 18px; height: 18px; flex-shrink: 0; color: var(--color-brand-600); opacity: 0; }
  .ss-option.selected .tick { opacity: 1; }
  /* With per-option icons the tick moves to the right edge so the icon owns
     the left slot. */
  .ss-options.has-icons .ss-option .tick { margin-left: auto; }

  /* Per-option / trigger icon slot. */
  .ss-icon {
    flex-shrink: 0; width: 20px; height: 20px;
    display: inline-grid; place-items: center; color: #6b7280;
  }
  .ss-option.selected .ss-icon { color: var(--color-brand-700); }
  .ss-icon :global(svg) { width: 18px; height: 18px; }
  .ss-trigger .ss-icon { color: #6b7280; }
  .ss-option.selected { color: var(--color-brand-800); font-weight: 600; background: var(--color-brand-50); }
  .ss-option.active { background: #f3f4f6; }
  .ss-option.selected.active { background: var(--color-brand-100); }
  .ss-option .label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .ss-option .sub { font-size: 12px; color: #9ca3af; }
  .ss-option.none { color: #6b7280; }

  .ss-empty { padding: 18px 12px; text-align: center; color: #9ca3af; font-size: 14px; }
</style>
